import React, { useState, useEffect, useRef, useContext } from "react";
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  makeStyles,
  Button,
  Box,
  Typography,
  Chip,
} from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { laboratoryServices } from "../services";
import { AuthContext } from "../context";
import Toast from "./toast";
import { capitalizeFirstLetterArray } from "../helpers";
import { consentsCaption } from "../screens/consent";
import moment from "moment";
function groupCosentsByUser(data) {
  return Object.values(
    data.reduce((acc, item) => {
      const id = item.rut;

      if (!acc[id]) {
        acc[id] = {
          rut: item.rut,
          firstName: item.firstName,
          lastName: item.lastName,
          email: item.email,
          signataryType: item.signataryType,
          consents: [],
        };
      }

      acc[id].consents.push({
        consentId: item.consentId,
        consentType: item.consentType,
        consentTarget: item.consentTarget,
        consentUrl: item.consentUrl,
        consentMajorVersion: item.consentMajorVersion,
        consentMinorVersion: item.consentMinorVersion,
        createdAt: item.createdAt,
        footprint: item.footprint,
      });

      return acc;
    }, {})
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    width: "100%",
    maxWidth: 400,
    display: "flex",
    gap: 8,
  },
  header_list: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    backgroundColor: `hsl(from ${theme.palette.primary.main} h s calc(65 + l))`,
  },
  list_item: {
    display: "grid",
    gap: 4,
    justifyItems: "center",
    padding: 0,
    paddingTop: 4,
    paddingBottom: 4,
    gridTemplateColumns: "repeat(auto-fit, minmax(70px, 1fr))",
    alignItems: "center",
    "&>span": {
      fontSize: "0.92rem",
      wordBreak: "break-all",
    },
  },
  list_item_header: {
    display: "grid",
    gap: 4,
    justifyItems: "center",
    padding: 0,
    paddingTop: 4,
    paddingBottom: 4,
    gridTemplateColumns: "repeat(auto-fit, minmax(70px, 1fr))",
    alignItems: "center",
    backgroundColor: `hsl(from ${theme.palette.primary.main} h s calc(69 + l))`,
    "&>span": {
      fontWeight: "600",
      fontSize: "0.85rem",
      wordBreak: "break-all",
    },
  },
}));

const ConsentsSearchBar = () => {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "" });
  const [open, setOpen] = useState(false);
  const [
    loadingDownloadSignedSignatories,
    setLoadingDownloadSignedSignatories,
  ] = useState(new Set());
  const { authState } = useContext(AuthContext);
  const searchedQuery = useRef("");
  const handleSearchUserConsents = async (e) => {
    e.preventDefault();
    try {
      if (searchedQuery.current === query) {
        if (results) {
          setOpen(true);
          return;
        } else
          throw new Error(
            "No encontramos usuarios que coincidan con tu búsqueda."
          );
      }
      setLoading(true);
      const { status, response } = await laboratoryServices.getUserConsents({
        token: authState.token,
        regex: query,
      });
      searchedQuery.current = query;
      if (status === 200) {
        const { signedConsents } = response;
        if (Array.isArray(signedConsents) && signedConsents.length > 0) {
          const data = groupCosentsByUser(signedConsents);
          setResults(data);
          setOpen(true);
        } else
          throw new Error(
            "No encontramos usuarios que coincidan con tu búsqueda."
          );
      } else
        throw new Error(
          response.message || "Error al obtener usuarios. Intente mas tarde"
        );
    } catch (e) {
      setToast({ open: true, message: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSignedSignatories = (consentId,footprint,type) => {
    setLoadingDownloadSignedSignatories((prev) => {
      const newSet = new Set(prev);
      newSet.add(consentId);
      return newSet;
    });
    laboratoryServices
      .downloadSignedSignatories({ consentId, footprint, type })
      .then((res) => {
        const { status, response } = res;

        if (status !== 200)
          throw new Error("Error al descargar el consentimiento.");
        // var blob = new Blob([response], {
        //   type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        // });
        // var link = document.createElement("a");
        // link.href = window.URL.createObjectURL(blob);
        // link.download = `Consentimientos_firmados_${moment().format('DD/MM/YYYY')}`;
        // link.click();
        // window.URL.revokeObjectURL(link.href);
        const url = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Consentimientos_firmados_${moment().format(
          "DD/MM/YYYY"
        )}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => {
        setToast({ open: true, message: e.message });
      })
      .finally(() => {
        setLoadingDownloadSignedSignatories((prev) => {
          const newSet = new Set(prev);
          newSet.delete(consentId);
          return newSet;
        });
      });
  };

  return (
    <div className={classes.container}>
      <form onSubmit={handleSearchUserConsents} className={classes.container}>
        <TextField
          label="Buscar médico o paciente"
          fullWidth
          variant="outlined"
          size="small"
          placeholder="nombre... rut... email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button
          size="small"
          disabled={loading}
          variant="outlined"
          color="primary"
          onClick={handleSearchUserConsents}
        >
          {loading ? <CircularProgress size={20} /> : "Buscar"}
        </Button>
      </form>
      <Toast
        open={toast.open}
        message={toast.message}
        close={() => setToast((prev) => ({ ...prev, open: false }))}
      />
      <Dialog
        PaperProps={{ style: { margin: 12, width: "calc(100% - 24px)" } }}
        fullWidth={true}
        maxWidth={"md"}
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="Consentimientos firmados "
      >
        <DialogTitle
          id="Consentimientos firmados "
          style={{ paddingLeft: 12, paddingRight: 12 }}
        >
          Consentimientos firmados
        </DialogTitle>
        <DialogContent style={{ padding: 0 }}>
          <DialogContentText style={{ paddingInline: 12, marginBottom: 0 }}>
            Resultados para <i>{query}</i>
          </DialogContentText>
          <List>
            {results?.map((r) => (
              <ListItem
                key={r.doctorId}
                style={{ flexDirection: "column", alignItems: "start" }}
              >
                <Box component={"span"} className={classes.header_list}>
                  <Box
                    component={"span"}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography component={"span"}>
                      {capitalizeFirstLetterArray(
                        `${r.firstName} ${r.lastName}`
                      )}
                    </Typography>
                    <Typography component={"span"} variant="caption">
                      {capitalizeFirstLetterArray(`${r.rut}`)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={
                      r.signataryType === "patient" ? "secondary" : "primary"
                    }
                    label={
                      r.signataryType === "patient" ? "Paciente" : "Médico"
                    }
                  />
                </Box>
                <List
                  style={{ width: "100%", paddingTop: "0", paddingBottom: 0 }}
                >
                  <ListItem className={classes.list_item_header}>
                    <Typography component={"span"}>Tipo</Typography>
                    <Typography component={"span"}>Objetivo</Typography>
                    <Typography component={"span"}>Version</Typography>
                  </ListItem>
                  {r.consents?.map((consent) => (
                    <ListItem
                      button
                      disabled={loadingDownloadSignedSignatories.has(
                        consent.consentId
                      )}
                      onClick={() =>
                        handleDownloadSignedSignatories(
                          consent.consentId,
                          consent.footprint,
                          r.signataryType
                        )
                      }
                      key={`${r.signataryType}-${r.rut}-${consent.consentId}`}
                      className={classes.list_item}
                    >
                      <Typography component={"span"}>
                        {consentsCaption.type[consent.consentType]}
                      </Typography>
                      <Typography component={"span"}>
                        {consentsCaption.target[consent.consentTarget]}
                      </Typography>
                      <Typography
                        component={"span"}
                      >{`${consent.consentMajorVersion}.${consent.consentMinorVersion}`}</Typography>
                    </ListItem>
                  ))}
                </List>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ConsentsSearchBar;
